import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { isTokenValid } from '../utils/tokenUtils';
import { useDispatch, useSelector } from 'react-redux';
import { logoutRequest } from '../redux/actions/authActions';
import InactivityWarning from '../components/authenthication/InactivityWarning';
import store from '../redux/store/store'
export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const refresh = useSelector((state) => state.auth.refreshToken);
  const access = useSelector((state) => state.auth.accessToken);
  const dispatch = useDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const showWarningRef = useRef(false);
  const timeoutId = useRef(null);
  const intervalId = useRef(null);
  const startTimerRef = useRef(null);
  const doLogoutRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && isTokenValid(token)) {
      setIsAuthenticated(true);
      // console.log("Token is valid, user is authenticated.");
    } else {
      console.log("No valid token found, user is not authenticated.");
    }
  }, [access]);

  const updateWarning = (val) => {
    showWarningRef.current = val;
    setShowWarning(val);
  };

  const clearTimers = () => {
    if (timeoutId.current) { clearTimeout(timeoutId.current); timeoutId.current = null; }
    if (intervalId.current) { clearInterval(intervalId.current); intervalId.current = null; }
  };

 const doLogout = useCallback(() => {
    clearTimers();
    updateWarning(false);
    sessionStorage.removeItem("last_activity");
    setIsAuthenticated(false);
    const authState = store.getState().auth;
    console.log('[doLogout] store.getState().auth at logout time:', authState);
    const { refresh: latestRefresh, access: latestAccess } = authState;
    dispatch(logoutRequest({ refresh: latestRefresh, access: latestAccess }));
}, [dispatch]);
  doLogoutRef.current = doLogout;


  const login = (token) => {
    localStorage.setItem('accessToken', token);
    sessionStorage.setItem("last_activity", Date.now().toString());
    updateWarning(false);
    setIsAuthenticated(true);
  };

  const logout = () => doLogoutRef.current();

  useEffect(() => {
    if (!isAuthenticated) return;
    const session_timeout = localStorage.getItem("session_minutes");
    const inactivityMinutes = Number(session_timeout);
    if (!inactivityMinutes || inactivityMinutes <= 0) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const isExpired = () => {
      const lastActivity = sessionStorage.getItem("last_activity");
      if (!lastActivity) return false;
      const elapsedMinutes = (Date.now() - Number(lastActivity)) / 1000 / 60;
      return elapsedMinutes >= inactivityMinutes;
    };
    const startTimer = () => {
      if (showWarningRef.current) return;
      if (timeoutId.current) clearTimeout(timeoutId.current);
      sessionStorage.setItem("last_activity", Date.now().toString());
      timeoutId.current = setTimeout(() => {
        updateWarning(true);
      }, inactivityMinutes * 60 * 1000);
    };

    startTimerRef.current = startTimer;

    // Same tab — ignore if warning showing, show warning if expired, else reset timer
    const handleActivity = () => {
      if (showWarningRef.current) return;
      if (isExpired()) { updateWarning(true); return; }
      startTimer();
    };

    // Tab switch or hibernate — direct logout if expired
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !showWarningRef.current && isExpired()) doLogoutRef.current();
    };

    const handleFocus = () => {
      if (showWarningRef.current) return;
      if (isExpired()) doLogoutRef.current();
    };

    // Catch background tab expiry every 15s
    intervalId.current = setInterval(() => {
      if (showWarningRef.current) return;
      if (isExpired()) {
        updateWarning(true);
      }
    }, 15 * 1000);

    events.forEach(e => document.addEventListener(e, handleActivity));
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    startTimer();

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimers();
    };
  }, [isAuthenticated]);

  const handleContinue = useCallback(() => {
    sessionStorage.setItem("last_activity", Date.now().toString());
    updateWarning(false);
    startTimerRef.current?.();
  }, []);

  const handleLogout = useCallback(() => {
    doLogoutRef.current();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
      <InactivityWarning open={showWarning} onContinue={handleContinue} onLogout={handleLogout} warningSeconds={15} />
    </AuthContext.Provider>
  );
};

export default AuthProvider;

